import React, { useContext, useEffect, useRef, useState, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import axios from 'axios'

import AppConfigContext from '../app_layout/app_config_context'
import LoadingWrapper from '../widgets/loading'
import BreakdownTable from './breakdown_table'
import ConcurrencyLimitEditor from './concurrency_limit_editor'
import { fetchFairUsageStats } from './helpers/fair_usage_service'
import { setTheme } from '../../reducers/userSlice'

import '../../static/css/data_pulse_fair_usage.css'
import '../../static/css/dark_mode_shell.css'

// User-selectable auto-refresh cadence (the dropdown in the refresh bar) -- 5 min stays the
// default, matching the reference dashboard mockup's own default poll interval.
const POLL_INTERVAL_OPTIONS = [
  { value: 5 * 1000, label: 'Auto-refresh: every 5 sec' },
  { value: 30 * 1000, label: 'Auto-refresh: every 30 sec' },
  { value: 60 * 1000, label: 'Auto-refresh: every 1 min' },
  { value: 3 * 60 * 1000, label: 'Auto-refresh: every 3 min' },
  { value: 5 * 60 * 1000, label: 'Auto-refresh: every 5 min' }
]
const DEFAULT_POLL_INTERVAL_MS = 5 * 60 * 1000

// Dark mode covers the whole shell (sidebar/header/content, dark_mode_shell.css) while the
// toggle to turn it on only ever appears on this page, and only for these two emails -- an
// internal preview, not a general feature yet.
const DARK_MODE_ALLOWED_EMAILS = ['aditya@socialfrontier.com', 'nishanth@socialfrontier.com']

// Pool-utilization warn/critical cutoffs -- not returned by the API, chosen explicitly here
// (the mockup only ever shows a sample 80% "warn" state, never states a critical cut).
const POOL_WARN_PCT = 80
const POOL_CRITICAL_PCT = 95

const formatDuration = (ms) => {
  const totalSeconds = Math.max(0, Math.round(ms / 1000))
  if (totalSeconds < 60) return totalSeconds + 's'
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return seconds === 0 ? minutes + 'm' : minutes + 'm ' + seconds + 's'
}

const formatClock = (ms) => new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })

const PoolTile = ({ pool }) => {
  const pct = pool.size > 0 ? Math.round((pool.in_flight / pool.size) * 100) : null
  const level = pct === null ? null : pct >= POOL_CRITICAL_PCT ? 'critical' : pct >= POOL_WARN_PCT ? 'warn' : null
  const qualifier = level === 'critical' ? ' — at capacity' : level === 'warn' ? ' — approaching capacity' : ''
  return (
    <div className='dpfu-tile'>
      <div className='dpfu-tile-label'>Pool utilization</div>
      <div className='dpfu-tile-value'>
        {pool.in_flight}<small> / {pool.size} in flight</small>
      </div>
      {pct === null ? (
        <div className='dpfu-tile-delta'>Pool size not configured</div>
      ) : (
        <>
          <div className='dpfu-meter-track'>
            <div
              className={'dpfu-meter-fill' + (level ? ' ' + level : '')}
              style={{ width: Math.min(pct, 100) + '%' }}
            />
          </div>
          <div className={'dpfu-tile-delta' + (level ? ' ' + level : '')}>{pct}% of pool{qualifier}</div>
        </>
      )}
    </div>
  )
}

const BacklogTile = ({ backlog, delta }) => (
  <div className='dpfu-tile'>
    <div className='dpfu-tile-label'>Queued backlog</div>
    <div className='dpfu-tile-value'>{backlog.count}</div>
    <div className='dpfu-tile-delta'>
      {delta === null
        ? 'No prior sample yet'
        : (delta.diffCount > 0 ? '↑ ' : delta.diffCount < 0 ? '↓ ' : '→ ') +
          Math.abs(delta.diffCount) + ' in the last ~' + formatDuration(delta.elapsedMs)}
    </div>
  </div>
)

const OldestPendingTile = ({ backlog }) => {
  const oldest = backlog.oldest_created_on ? new Date(backlog.oldest_created_on) : null
  const ageMs = oldest ? Date.now() - oldest.getTime() : null
  return (
    <div className='dpfu-tile'>
      <div className='dpfu-tile-label'>Oldest pending</div>
      <div className='dpfu-tile-value'>{ageMs === null ? 'None' : formatDuration(ageMs)}</div>
      <div className='dpfu-tile-delta'>{ageMs === null ? 'Nothing queued' : 'age of the oldest queued request'}</div>
    </div>
  )
}

const ActiveEntitiesTile = ({ entities }) => {
  const total = entities.total_active_users + entities.total_active_orgs
  return (
    <div className='dpfu-tile'>
      <div className='dpfu-tile-label'>Active users & orgs</div>
      <div className='dpfu-tile-value'>{total}</div>
      <div className={'dpfu-tile-delta' + (entities.waiting_for_slot_count > 0 ? ' warn' : '')}>
        {entities.total_active_users} users · {entities.total_active_orgs} orgs —{' '}
        {entities.waiting_for_slot_count} waiting for a slot
      </div>
    </div>
  )
}

/*
 * Phase 14b: the real visuals on top of 14a's data-fetch scaffold -- a pool-utilization meter,
 * the per-user/org breakdown table (with status pills), and a backlog-count delta computed
 * client-side between consecutive polls. Entity rows are id/type only (13a's own design
 * decision -- no batch name-lookup endpoint exists to resolve them; see fair_usage_service.js).
 */
const DataPulseFairUsage = () => {
  const appConfig = useContext(AppConfigContext)
  const client = appConfig.client
  const dispatch = useDispatch()
  const userEmail = useSelector((state) => state.user.email)
  const theme = useSelector((state) => state.user.theme)
  const canToggleTheme = DARK_MODE_ALLOWED_EMAILS.includes(userEmail)

  const [stats, setStats] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [lastUpdated, setLastUpdated] = useState(null)
  const [backlogDelta, setBacklogDelta] = useState(null)
  const [pollIntervalMs, setPollIntervalMs] = useState(DEFAULT_POLL_INTERVAL_MS)

  const controllerRef = useRef(null)
  const prevBacklogRef = useRef(null)

  // Only a whitelisted operator's own preference is ever honored -- a 'dark' value left over in
  // Redux state from a prior session must not silently apply to someone else's session.
  const resolvedTheme = canToggleTheme ? theme : 'light'

  // Mirror the resolved theme onto <body> so the shell (sidebar/header/#mainContent,
  // dark_mode_shell.css) goes dark along with this page's own content -- and always clear it on
  // unmount, so leaving this page (the only place the toggle exists) restores the normal light
  // shell rather than leaving every other, non-dark-styled page sitting under a dark sidebar.
  useEffect(() => {
    if (resolvedTheme === 'dark') {
      document.body.setAttribute('data-theme', 'dark')
    } else {
      document.body.removeAttribute('data-theme')
    }

    return () => {
      document.body.removeAttribute('data-theme')
    }
  }, [resolvedTheme])

  const load = useCallback(() => {
    if (controllerRef.current) controllerRef.current.abort('refetch')
    const controller = new AbortController()
    controllerRef.current = controller
    setIsLoading(true)
    fetchFairUsageStats(client, { top_n: 50 }, controller.signal)
      .then((data) => {
        const now = Date.now()
        const prev = prevBacklogRef.current
        // Delta vs the previous successful poll -- no delta on the very first sample, no
        // client-side history array kept beyond the one prior value (nothing charts this yet).
        setBacklogDelta(prev ? { diffCount: data.backlog.count - prev.count, elapsedMs: now - prev.timestamp } : null)
        prevBacklogRef.current = { count: data.backlog.count, timestamp: now }
        setStats(data)
        setError(null)
        setLastUpdated(now)
      })
      .catch((err) => {
        if (!axios.isCancel(err)) setError('Failed to load fair-usage stats.')
      })
      .finally(() => setIsLoading(false))
  }, [client])

  // Re-mounts the timer (not just on load()) whenever pollIntervalMs changes, so picking a
  // shorter/longer cadence in the dropdown takes effect immediately instead of waiting out
  // whatever interval was already running.
  useEffect(() => {
    load()
    const intervalId = setInterval(load, pollIntervalMs)
    return () => {
      clearInterval(intervalId)
      if (controllerRef.current) controllerRef.current.abort('unmount')
    }
  }, [load, pollIntervalMs])

  return (
    <div
      className='cocrm-data-pulse-fair-usage overflow-auto'
      data-theme={resolvedTheme}
    >
      <div className='dpfu-page-head'>
        <div>
          <h1 className='co-page-title'>Data Pulse Fair Usage</h1>
          <p className='co-page-sub'>Realtime admission scheduler — live pool occupancy and backlog.</p>
        </div>
        <div className='dpfu-refresh-bar'>
          <span className='dpfu-last-updated'>
            {lastUpdated ? 'Last updated ' + formatClock(lastUpdated) : ''}
          </span>
          <select
            className='dpfu-interval-select'
            title='Auto-refresh interval'
            aria-label='Auto-refresh interval'
            value={pollIntervalMs}
            onChange={(e) => setPollIntervalMs(Number(e.target.value))}
          >
            {POLL_INTERVAL_OPTIONS.map((option) => (
              <option
                key={option.value}
                value={option.value}
              >
                {option.label}
              </option>
            ))}
          </select>
          {canToggleTheme && (
            <button
              type='button'
              className='dpfu-theme-toggle'
              title={resolvedTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              aria-label='Toggle theme'
              onClick={() => dispatch(setTheme(theme === 'dark' ? 'light' : 'dark'))}
            >
              <i className={'fa-solid ' + (resolvedTheme === 'dark' ? 'fa-sun' : 'fa-moon')} />
            </button>
          )}
          <button
            type='button'
            className='dpfu-refresh-btn'
            title='Refresh'
            aria-label='Refresh'
            disabled={isLoading}
            onClick={load}
          >
            <i className={'fa-solid fa-rotate-right' + (isLoading ? ' fa-spin' : '')} />
          </button>
        </div>
      </div>
      {error && <p className='dpfu-tile-delta critical'>{error}</p>}
      {!stats ? (
        <div className='d-flex justify-content-center py-5'>
          <LoadingWrapper
            type='spin'
            color='#9aa0a6'
            height={40}
            width={40}
          />
        </div>
      ) : (
        <>
          <section
            className='dpfu-section'
            aria-label='Key stats'
          >
            <h2 className='dpfu-section-title'>Right now</h2>
            <div className='dpfu-kpi-row'>
              <PoolTile pool={stats.pool} />
              <BacklogTile
                backlog={stats.backlog}
                delta={backlogDelta}
              />
              <OldestPendingTile backlog={stats.backlog} />
              <ActiveEntitiesTile entities={stats.entities} />
            </div>
          </section>
          <section
            className='dpfu-section'
            aria-label='Per-user and per-org breakdown'
          >
            <h2 className='dpfu-section-title'>
              Per-user / per-org breakdown{' '}
              <span className='dpfu-count-badge'>
                ({stats.entities.total_active_users} users, {stats.entities.total_active_orgs} orgs —{' '}
                {stats.entities.waiting_for_slot_count} waiting for a slot)
              </span>
            </h2>
            <BreakdownTable
              client={client}
              rows={stats.entities.top}
              totalActive={stats.entities.total_active_users + stats.entities.total_active_orgs}
            />
            <div className='dpfu-legend-row'>
              <div className='dpfu-legend-item'>
                <span
                  className='dot'
                  style={{ background: 'var(--dpfu-good)' }}
                />
                <strong>Full share</strong> — no pending requests left
              </div>
              <div className='dpfu-legend-item'>
                <span
                  className='dot'
                  style={{ background: 'var(--dpfu-warning)' }}
                />
                <strong>Capped, more waiting</strong> — getting a fair share, more queued behind it
              </div>
              <div className='dpfu-legend-item'>
                <span
                  className='dot'
                  style={{ background: 'var(--dpfu-critical)' }}
                />
                <strong>Waiting for a slot</strong> — zero in flight despite pending work
              </div>
            </div>
          </section>
          <section
            className='dpfu-section'
            aria-label='Update concurrency limits'
          >
            <h2 className='dpfu-section-title'>Update concurrency limits</h2>
            <ConcurrencyLimitEditor client={client} />
          </section>
        </>
      )}
    </div>
  )
}

export default DataPulseFairUsage
